import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { AppError } from 'src/app/core/models/api-error';
import { ErrorMessageService } from 'src/app/core/services/error-message.service';
import { SelectOption } from 'src/app/ui/form-field/form-field.component';
import { SharedDialogOptions } from 'src/app/ui/shared-dialog/shared-dialog.component';

import {
  MfaPolicy,
  MfaPolicySettings,
  MfaPolicyUpdate,
} from '../../../models/mfa.models';
import { MfaService } from '../../../services/mfa.service';

export interface MfaPolicyDialogData {
  settings: MfaPolicySettings;
  /** People with no factor, named in the confirmation. */
  coverageGap: number;
  /** False leaves the form and the save disabled: the PATCH answers 403, and
   *  a form that looks live is worse than one that says it is not. */
  canEdit: boolean;
}

/**
 * The tenant MFA policy form, and the confirmation that guards requiring it.
 *
 * The confirmation is a step in this dialog rather than a second one stacked
 * on top: the form is disabled while it is up, so what the user confirmed is
 * necessarily what gets saved. It closes with the saved settings, or with
 * `undefined` when nothing was written.
 */
@Component({
  selector: 'app-mfa-policy-dialog',
  templateUrl: './mfa-policy-dialog.component.html',
  styleUrls: ['./mfa-policy-dialog.component.scss'],
})
export class MfaPolicyDialogComponent implements OnInit, OnDestroy {
  static readonly MAX_GRACE_DAYS = 365;

  readonly settings: MfaPolicySettings;
  readonly coverageGap: number;
  readonly canEdit: boolean;

  policyForm!: FormGroup;
  /** Outside the form group: `ui-checkbox` is not a value accessor. */
  emailOtpEnabled = false;

  saving = false;
  /** The confirmation step, not a nested dialog. */
  confirming = false;
  error: AppError | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) data: MfaPolicyDialogData,
    private readonly dialogRef: MatDialogRef<
      MfaPolicyDialogComponent,
      MfaPolicySettings | undefined
    >,
    private readonly fb: FormBuilder,
    private readonly mfa: MfaService,
    private readonly translate: TranslateService,
    private readonly errorMessages: ErrorMessageService
  ) {
    this.settings = data.settings;
    this.coverageGap = data.coverageGap;
    this.canEdit = data.canEdit;
  }

  ngOnInit(): void {
    this.policyForm = this.fb.group({
      policy: [this.settings.policy],
      grace_days: [
        this.settings.grace_days,
        Validators.compose([
          Validators.required,
          Validators.min(0),
          Validators.max(MfaPolicyDialogComponent.MAX_GRACE_DAYS),
        ]),
      ],
    });
    this.emailOtpEnabled = this.settings.email_otp_enabled;

    if (!this.canEdit) {
      this.policyForm.disable({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- chrome --------------------------------------------------------------

  get title(): string {
    return this.translate.instant(
      this.confirming
        ? 'settings_security.policy.require_confirm_title'
        : 'settings_security.policy.title'
    );
  }

  get dialogOptions(): SharedDialogOptions {
    return {
      size: 'medium',
      confirmText: this.translate.instant(
        this.confirming
          ? 'settings_security.policy.require_confirm_action'
          : 'settings_security.policy.save'
      ),
      confirmButtonStyle: this.confirming ? 'danger' : 'primary',
      // `invalid` is false while confirming -- the form is disabled by then,
      // so this reads the live form only.
      confirmDisabled:
        !this.canEdit || this.saving || !this.hasChanges || this.policyForm.invalid,
      confirmLoading: this.saving,
      cancelText: this.translate.instant(
        this.confirming ? 'settings_security.policy.confirm_back' : 'common.cancel'
      ),
    };
  }

  /** Translated here because `SelectOption.label` is a plain string, out of
   *  reach of the template's translate pipe. */
  get policyOptions(): SelectOption[] {
    const policies: MfaPolicy[] = ['off', 'optional', 'required'];
    return policies.map((policy) => ({
      value: policy,
      label: this.translate.instant(`settings_security.policy.value.${policy}`),
    }));
  }

  // --- the diff ------------------------------------------------------------

  /** Only the fields that differ from the settings this dialog opened on. */
  get pendingChanges(): MfaPolicyUpdate {
    const changes: MfaPolicyUpdate = {};
    const policy = this.policyForm.controls['policy'].value as MfaPolicy;
    const graceDays = Number(this.policyForm.controls['grace_days'].value);

    if (policy !== this.settings.policy) {
      changes.policy = policy;
    }
    if (graceDays !== this.settings.grace_days) {
      changes.grace_days = graceDays;
    }
    if (this.emailOtpEnabled !== this.settings.email_otp_enabled) {
      changes.email_otp_enabled = this.emailOtpEnabled;
    }
    return changes;
  }

  get hasChanges(): boolean {
    return Object.keys(this.pendingChanges).length > 0;
  }

  /** The pending save would strand users with no factor. */
  get willLockOutUnenrolled(): boolean {
    return MfaPolicyDialogComponent.locksOutUnenrolled(
      this.pendingChanges,
      this.settings
    );
  }

  /** Whether *changes* leave the tenant requiring a factor on a window that
   *  has run out for anyone unenrolled. */
  private static locksOutUnenrolled(
    changes: MfaPolicyUpdate,
    settings: MfaPolicySettings
  ): boolean {
    const policy = changes.policy ?? settings.policy;
    if (policy !== 'required') {
      return false;
    }
    // A change that only relaxes the window is not the hazard.
    const graceDays = changes.grace_days ?? settings.grace_days;
    return changes.policy === 'required' || graceDays < settings.grace_days;
  }

  // --- actions -------------------------------------------------------------

  onEmailOtpChange(checked: boolean): void {
    this.emailOtpEnabled = checked;
  }

  /**
   * Saves, stopping once at the confirmation when the result would lock
   * people out. Keyed on the policy the save leaves behind, not the change:
   * cutting `grace_days` on an already-`required` tenant locks people out
   * too.
   */
  submit(): void {
    if (!this.canEdit || this.saving || this.policyForm.invalid) {
      return;
    }
    const changes = this.pendingChanges;
    if (!Object.keys(changes).length) {
      return;
    }
    if (!this.confirming && MfaPolicyDialogComponent.locksOutUnenrolled(changes, this.settings)) {
      // Frozen, not merely warned about: the form cannot move between the
      // confirmation and the save that follows it.
      this.confirming = true;
      this.policyForm.disable({ emitEvent: false });
      return;
    }
    this.save(changes);
  }

  /** Back out of the confirmation, or close. Also what the header × does. */
  onCancel(): void {
    // Not while the save is in flight. `app-shared-dialog` keeps its × and its
    // cancel button live regardless, and backing out here would re-enable the
    // form under a request that is still going to land -- the user would type
    // into a form that the arriving response then closes, silently keeping the
    // values they thought they had abandoned.
    if (this.saving) {
      return;
    }
    if (this.confirming) {
      this.leaveConfirmation();
      return;
    }
    this.dialogRef.close(undefined);
  }

  private leaveConfirmation(): void {
    this.confirming = false;
    if (this.canEdit) {
      this.policyForm.enable({ emitEvent: false });
    }
  }

  private save(changes: MfaPolicyUpdate): void {
    this.error = null;
    this.saving = true;
    this.mfa
      .updatePolicySettings(changes)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: (response) => this.dialogRef.close(response.object ?? this.settings),
        // Stays open on the rejected values, so the error message has
        // something visible to refer to.
        error: (error) => {
          this.leaveConfirmation();
          this.error = this.errorMessages.parse(error as never);
        },
      });
  }
}
